import React from 'react';
import { Dialog } from '@fluentui/react/lib/Dialog';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { SearchBox } from '@fluentui/react/lib/SearchBox';
import { ResponsiveMode } from '@fluentui/react';
import { InView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';

import { _dataProvider, IUser } from '../dataProvider/DataProvider';
import { UserProfile } from '../SharedCommon/UserProfile';
import { AvatarImage } from '../SharedCommon/UserAvatarName';
import AddNewContactWindow from './AddNewContactWindow';
import type { IContactItem } from './types';
import backIcon from '../img/icon-Back.png';
import closeIcon from '../img/close.png';
import plusIcon from '../img/icon-Plus.png';
import { COMMON_MODAL_LIST_STYLE, getDialogThemeClassName, getThemedModalMainStyles } from './dialogStyles';
import {
  COMMON_DIALOG_CONTENT_STYLES,
  COMMON_HEADER_BUTTON_STYLES,
  getSearchBoxStyles,
  getGroupButtonStyles
} from './shellStyles';
import { SearchBoxChrome } from './SearchBoxChrome';
import { useIdigestChatTheme } from '../ThemeContext';
import './Dialog.css';

export interface IPromptContactListProps {
  isOpen: boolean;
  onDismiss: () => void;
  contactList: IContactItem[];
  currentUser: IUser;
  launchOneOnOneWithFriend: any;
}

export const PromptContactList: React.FC<IPromptContactListProps> = (props) => {
  const { t } = useTranslation();
  const { theme } = useIdigestChatTheme();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [showFriend, setShowFriend] = React.useState<IUser>();
  const [showAddNewContact, setShowAddNewContact] = React.useState(false);

  const searchBoxStyles = React.useMemo(() => getSearchBoxStyles(theme), [theme]);
  const groupButtonStyles = React.useMemo(() => getGroupButtonStyles(theme), [theme]);

  const filteredContacts = props.contactList.filter((contact) => {
    return !searchQuery || (contact.name || contact.displayName).toLowerCase().includes(searchQuery.toLowerCase());
  });

  const groupedContacts = filteredContacts.reduce(
    (groups, contact) => {
      const letter = (contact.displayName || contact.name || '?')[0].toUpperCase();
      if (!groups[letter]) {
        groups[letter] = [];
      }
      groups[letter].push(contact);
      return groups;
    },
    {} as Record<string, IContactItem[]>
  );

  const handleDismiss = () => {
    setShowFriend(undefined);
    props.onDismiss();
  };

  const renderHeader = () => {
    if (showFriend) {
      return (
        <div className='header-section'>
          <div className='button-wrapper left-btn'>
            <DefaultButton
              className='header-button'
              styles={COMMON_HEADER_BUTTON_STYLES}
              onClick={() => setShowFriend(undefined)}
            >
              <img src={backIcon} alt='Back' className='button-icon' />
            </DefaultButton>
            <span className='button-label'>{t('Back')}</span>
          </div>
          <div className='button-wrapper right-btn'>
            <DefaultButton className='header-button' styles={COMMON_HEADER_BUTTON_STYLES} onClick={handleDismiss}>
              <img src={closeIcon} alt='Close' className='button-icon' />
            </DefaultButton>
            <span className='button-label'>{t('Close')}</span>
          </div>
        </div>
      );
    }

    return (
      <div className='header-section'>
        <div className='button-wrapper left-btn'>
          <DefaultButton className='header-button' styles={COMMON_HEADER_BUTTON_STYLES} onClick={handleDismiss}>
            <img src={backIcon} alt='Back' className='button-icon' />
          </DefaultButton>
          <span className='button-label'>{t('Back')}</span>
        </div>
        <div className='button-wrapper right-btn'>
          <DefaultButton
            className='header-button'
            styles={COMMON_HEADER_BUTTON_STYLES}
            onClick={() => setShowAddNewContact(true)}
          >
            <img src={plusIcon} alt='Add Contact' className='button-icon' />
          </DefaultButton>
          <span className='button-label'>{t('AddNewContact')}</span>
        </div>
        <h3 className='header-title' style={{ fontWeight: 600, fontSize: '18px', margin: '0 0 4px' }}>
          {t('Contact')}
        </h3>
      </div>
    );
  };

  return (
    <Dialog
      hidden={!props.isOpen}
      responsiveMode={ResponsiveMode.small}
      onDismiss={handleDismiss}
      dialogContentProps={{
        showCloseButton: false,
        styles: {
          inner: { padding: '0px' },
          header: { height: '0px' },
          content: COMMON_DIALOG_CONTENT_STYLES
        }
      }}
      modalProps={{
        isBlocking: false,
        styles: { main: getThemedModalMainStyles(theme) },
        containerClassName: `${getDialogThemeClassName(theme)}${showFriend ? '' : ' modal-list-fill'}`
      }}
    >
      {renderHeader()}
      {!showFriend ? (
        <>
          <div className='dialog-searchbox-wrapper searchbox-wrapper group-list-pane-pad'>
            <SearchBoxChrome>
              <SearchBox
                styles={searchBoxStyles}
                placeholder={t('Search')}
                value={searchQuery}
                onChange={(_, value) => setSearchQuery(value || '')}
                onClear={() => setSearchQuery('')}
              />
            </SearchBoxChrome>
          </div>
          <div
            className='contact-list fellowshipGroup idigest-chat-modal-list grouplist-wrapper group-list-pane-pad'
            style={COMMON_MODAL_LIST_STYLE}
            data-is-scrollable='true'
          >
            {Object.keys(groupedContacts)
              .sort()
              .map((letter) => (
                <div key={letter}>
                  {groupedContacts[letter]
                    .filter((user) => user.loginId !== props.currentUser.uniqueId)
                    .map((contact) => (
                      <InView key={contact.userId} triggerOnce>
                        {({ inView, ref }) => (
                          <ul ref={ref} className='ulGroups group-list-item-row'>
                            <div className='group-list-item-inner'>
                              <DefaultButton
                                className='orgGroupButton'
                                styles={{
                                  ...groupButtonStyles,
                                  flexContainer: {
                                    ...groupButtonStyles.flexContainer,
                                    minWidth: 0
                                  },
                                  root: {
                                    ...groupButtonStyles.root,
                                    padding: '10px 16px',
                                    width: 'auto',
                                    flex: '1 1 0',
                                    minWidth: 0,
                                    maxWidth: '100%'
                                  }
                                }}
                                onClick={() => {
                                  const user = contact as unknown as IUser;
                                  user.tag = _dataProvider.getMyTagForUser(user.userId);
                                  user.block = _dataProvider.getMyBlockForUser(user.userId);
                                  setShowFriend(user);
                                }}
                              >
                                {inView && <AvatarImage userId={contact.userId} className='groupSymbol' />}
                                <div className='group-list-item-label' title={contact.name || contact.displayName}>
                                  {contact.name || contact.displayName}
                                </div>
                              </DefaultButton>
                            </div>
                          </ul>
                        )}
                      </InView>
                    ))}
                </div>
              ))}
          </div>
        </>
      ) : (
        <div style={{ padding: '0 12px 8px' }}>
          <UserProfile
            user={showFriend}
            updateFrameStyle={{ marginTop: '20px' }}
            launchOneOnOneWithFriend={props.launchOneOnOneWithFriend}
            onDismiss={handleDismiss}
          />
        </div>
      )}
      {showAddNewContact && (
        <AddNewContactWindow
          onClose={() => setShowAddNewContact(false)}
          launchOneOnOneWithFriend={props.launchOneOnOneWithFriend}
        />
      )}
    </Dialog>
  );
};
